// shabba-dashboard/src/context/MarketContext.tsx

import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import type {
  OrderBookEntry, ParsedMessage, Trade, Position,
  Stats, SymbolStats, PriceMap, BookState, BookAnalytics, Order, OrderFill, PricePoint, AnalyticsData, BookChangePacket,
  QuoteSuggestion, QuoteDecisionRequest,
  InventoryState, SettlementReport, PerformanceSnapshot, RiskEvent,
  ActivityEvent, RegimeState, SprintState, DailySummary, MonthlySummary,
  EngineConfig, ConfigAckMessage
} from '../types';
import type { Alert, CommandAckMessage } from '../types/control';
import type { EngineRegime } from '../types';
import { INSTRUMENTS, labelForSecId } from '../constants/instruments';
import { useFixWebSocket } from '../hooks/useFixWebSocket';
import { useAuth } from './AuthContext';
import { parseFixMessage } from '../utils/parseFix';
import { applySnapshot, applyIncremental, bookToArrays, bestPrices } from '../utils/book';
import { eatNow, eatOpenToday, fmtHHmm, sameEatDay } from '../utils/time';
import type { EnhancedAnalyticsData } from '../types';

import { fetchTradeSeries } from '../api/series';
import { ReplayManager } from '../utils/replayManager';
import { dispatchMessage } from './messageHandlers/index';
import type { MessageCallbacks } from './messageHandlers/index';
import { useAppDispatch } from '../store/hooks';
import { setTwapPoints, setVwapPoints, setMicropricePoints, setLastPricePoints } from '../store/chartDataSlice';

import type { VWAPDataPoint } from '../components/PriceAreaChart';

let _activitySeq = 0;

function eatISO(d: Date) {
  // EAT = UTC+3; adjust if your runtime isn’t already in EAT
  // If your server/local is already EAT, you can just return d.toISOString()
  return new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString();
}
function startOfTodayISO() {
  const now = new Date();
  const s = new Date(now);
  s.setHours(0,0,0,0);
  return eatISO(s);
}
function nowISO() {
  return eatISO(new Date());
}

/** Minimal analytics shape so SIGNALS/ML_PREDICTION can populate regime/fill when no ANALYTICS received yet. */
function getMinimalAnalytics(): EnhancedAnalyticsData {
  return {
    depthImbalance: { topN: 5, bidVolume: 0, askVolume: 0, imbalance: 0, imbalancePercent: 0 },
    microprice: { microprice: 0, bidPrice: 0, askPrice: 0, bidSize: 0, askSize: 0, spread: 0 },
    staleness: { lastUpdateAge: 0, lastTradeAge: 0, isStale: false, stalenessLevel: 'fresh' },
    orderFlowImbalance: { buyFlow: 0, sellFlow: 0, netFlow: 0, flowRatio: 0, timeWindow: 300 },
    fillProbability: { probability: 0, queuePosition: 0, estimatedWaitTime: 0, confidence: 'low' },
    volatility: { realizedVol: 0, impliedVol: 0, volatilityPercentile: 0, recommendedSpread: 0, spreadMultiplier: 1.2 },
    liquidityHeatmap: { priceLevels: [], maxLiquidity: 0, avgLiquidity: 0 },
    vwapAnalysis: { currentVWAP: 0, distance: 0, distancePercent: 0, volume: 0, trend: 'neutral' },
    bookPressure: { pressure: 0, buyPressure: 0, sellPressure: 0, netPressure: 0, confidenceLevel: 'low' },
    spreadAnalysis: { currentSpread: 0, averageSpread: 0, spreadPercentile: 0, isWide: false, competitiveness: 'normal', optimalBid: 0, optimalAsk: 0 },
    riskMetrics: { inventoryRisk: 0, priceRisk: 0, liquidityRisk: 0, overallRisk: 'low', maxExposure: 0, recommendedPosition: 0 },
    fullBookImbalance: { bidVolume: 0, askVolume: 0, imbalance: 0, imbalancePercent: 0 },
    timestamp: Date.now()
  };
}

export type MarketContextValue = {
  // Symbol selection
  selectedSymbol: string;
  setSelectedSymbol: (s: string) => void;

  // Connection state
  connectionStatus: 'connecting' | 'connected' | 'disconnected';
  isAutoTrading: boolean;
  setIsAutoTrading: (b: boolean) => void;

  // Market data
  lastUpdateTime: string;
  currentPrice: number;
  stats: Stats;
  orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] };
  /**
   * Top-of-book for EVERY counter, reactively -- unlike `orderBook`, which is the selected
   * symbol only. Held as state rather than a ref because the measured book rate is 0.35 msg/s
   * across all six counters (one update per counter per ~17s), so the render cost the refs were
   * defending against is three orders of magnitude below what refs are for.
   */
  bookAnalyticsBySecId: Map<string, BookAnalytics>;
  /** Session stats for EVERY counter, reactively. `stats` is the selected symbol only. */
  statsBySecId: Map<string, SymbolStats>;
  priceMap: PriceMap;
  priceHistory: PricePoint[];
  vwapHistory: VWAPDataPoint[];
  analytics: EnhancedAnalyticsData | null;

  // Trading data
  positions: Position[];
  recentTrades: Trade[];
  orders: Order[];
  messages: ParsedMessage[];
  bookChanges: BookChangePacket[];
  
  // Portfolio and risk (calculated on backend)
  portfolio: {
    totalUnrealizedPnL: number;
    totalRealizedPnL: number;
    totalPnL: number;
    totalExposure: number;
  };
  risk: {
    grossNotional: number;
    longExposure: number;
    shortExposure: number;
    netExposure: number;
    capitalUtilization: number;
    priceSensitivity1pct: number;
    priceSensitivity5pct: number;
    realizedVolRisk: number;
    inventoryByTicker: Record<string, {
      quantity: number;
      settled_quantity: number;
      utilization: number;
      exit_ratio: number;
      price_sensitivity_1pct: number;
      price_sensitivity_5pct: number;
      capital_allocated: number;
      capital_used_pct: number;
      vol_risk_1sigma: number;
    }>;
  };

  // Quote suggestions
  quoteSuggestions: QuoteSuggestion[];
  sendQuoteDecision: (quoteId: string, accepted: boolean, reason?: string) => boolean;
  sendEngineConfig: (config: Partial<EngineConfig>) => boolean;
  engineConfig: EngineConfig | null;
  configAckStatus: 'idle' | 'pending' | 'ok' | 'error';
  configAckTime: string | null;
  configAckErrors: Record<string, string> | null;

  // Engine state (inventory, settlement, performance, risk events)
  inventoryBySymbol: Map<string, InventoryState>;
  settlementReport: SettlementReport | null;
  performanceSnapshot: PerformanceSnapshot | null;
  riskEvents: RiskEvent[];

  // Phase 2: live event narrative
  activityFeed: ActivityEvent[];
  regimeBySymbol: Map<string, RegimeState>;
  sprintState: SprintState | null;
  dailySummary: DailySummary | null;
  monthlySummary: MonthlySummary | null;
  dismissDailySummary: () => void;

  // Control plane alerts
  alerts: Alert[];
  dismissAlert: (id: string) => void;
  lastCommandAck: CommandAckMessage | null;

  // ML model health
  modelHealth: {
    fill_prob_accuracy?: number;
    fill_prob_mae?: number;
    wait_time_mae?: number;
    calibration_error?: number;
    predictions_count?: number;
    last_updated?: string;
    model_type?: string;
    drift_detected?: boolean;
  } | null;

  // REAL_DSE mode: Dar VPS health + FIX session status
  darHealth: {
    fixLoggedOn: boolean;
    fixSessionStatus: 'CONNECTED' | 'DISCONNECTED' | 'RECONNECTING' | 'UNKNOWN';
    lastHeartbeatAt: string | null;   // ISO timestamp of last HEARTBEAT from Dar
    pendingOrders: number;
    instrumentsLoaded: number;
    sessionId: string | null;
  };


  // Actions
  onRequestData: () => void;
  placeOrder: (order: {
    clientOrderId: string;
    symbol: string;
    side: 'BUY' | 'SELL';
    orderType: 'LIMIT' | 'MARKET';
    price?: number;
    quantity: number;
    timeInForce?: number;
    expireDate?: string;
    expireTime?: string;
  }) => void;
  cancelOrder: (orderId: string, symbol: string) => void;
  updateOrderTif: (orderId: string, timeInForce: number, expireDate?: string, expireTime?: string) => void;
  testWebSocket: () => void;

  // Chart data is now in Redux store — use useTwapData/useVwapData/useMicropriceData/useLastPriceData from src/store/hooks
  analyticsImbalanceBySymbol: Map<string, Array<{ t: number; depthImbalance: number; ofi: number }>>;
  getAnalyticsHistory: (secId: string) => Array<{ t: number; depthImbalance: number; ofi: number }>;

  // Constants and utilities
  INSTRUMENTS: typeof INSTRUMENTS;
  labelForSecId: (s: string) => string;
};

const MarketContext = createContext<MarketContextValue | null>(null);

// ---------------------------------------------------------------------------
// Domain sub-contexts — memoized slices of MarketContext state.
// Components can subscribe to only the slice they need, preventing re-renders
// from unrelated state changes.  useMarket() still works unchanged.
// ---------------------------------------------------------------------------

export type ConnectionContextValue = Pick<MarketContextValue,
  'connectionStatus' | 'messages' | 'darHealth' | 'testWebSocket'
>;

export type MarketDataContextValue = Pick<MarketContextValue,
  'selectedSymbol' | 'setSelectedSymbol' | 'lastUpdateTime' | 'currentPrice' |
  'stats' | 'orderBook' | 'priceMap' | 'priceHistory' | 'vwapHistory' | 'analytics' |
  'recentTrades' | 'bookChanges' | 'analyticsImbalanceBySymbol' | 'getAnalyticsHistory' |
  'bookAnalyticsBySecId' | 'statsBySecId' |
  'INSTRUMENTS' | 'labelForSecId'
>;

export type TradingContextValue = Pick<MarketContextValue,
  'isAutoTrading' | 'setIsAutoTrading' | 'positions' | 'orders' | 'portfolio' | 'risk' |
  'placeOrder' | 'cancelOrder' | 'updateOrderTif' | 'onRequestData'
>;

export type EngineContextValue = Pick<MarketContextValue,
  'quoteSuggestions' | 'sendQuoteDecision' | 'inventoryBySymbol' | 'settlementReport' |
  'performanceSnapshot' | 'riskEvents' | 'activityFeed' | 'regimeBySymbol' | 'sprintState' |
  'dailySummary' | 'monthlySummary' | 'dismissDailySummary' | 'modelHealth' |
  'engineConfig' | 'configAckStatus' | 'configAckTime' | 'configAckErrors' | 'sendEngineConfig'
>;

const ConnectionCtx = createContext<ConnectionContextValue | null>(null);
const MarketDataCtx = createContext<MarketDataContextValue | null>(null);
const TradingCtx    = createContext<TradingContextValue | null>(null);
const EngineCtx     = createContext<EngineContextValue | null>(null);

export function MarketProvider({ children }: { children: React.ReactNode }) {
  // State
  const [selectedSymbol, setSelectedSymbol] = useState<string>('LPPJ');
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const [isAutoTrading, setIsAutoTrading] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>('');
  const [currentPrice, setCurrentPrice] = useState(0);
  const [priceHistory, setPriceHistory] = useState<PricePoint[]>([]);
  const [vwapHistory, setVwapHistory] = useState<VWAPDataPoint[]>([]);
  const [priceMap, setPriceMap] = useState<PriceMap>({});
  const [stats, setStats] = useState<Stats>({ _cumPxSz: 0, _cumSz: 0 });
  const [orderBook, setOrderBook] = useState<{ bids: OrderBookEntry[]; asks: OrderBookEntry[] }>({ bids: [], asks: [] });
  const [analytics, setAnalytics] = useState<EnhancedAnalyticsData | null>(null);
  const [messages, setMessages] = useState<ParsedMessage[]>([]);
  const [bookChanges, setBookChanges] = useState<BookChangePacket[]>([]);
  const [recentTrades, setRecentTrades] = useState<Trade[]>([
    // { time: '10:02:15', price: 1215.50, size: 500, side: 'buy' },
    // { time: '10:01:42', price: 1214.25, size: 750, side: 'sell' },
    // { time: '10:01:18', price: 1216.75, size: 300, side: 'buy' },
    // { time: '10:00:55', price: 1213.00, size: 1000, side: 'sell' },
    // { time: '10:00:31', price: 1215.25, size: 425, side: 'buy' },
  ]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [portfolio, setPortfolio] = useState<{
    totalUnrealizedPnL: number;
    totalRealizedPnL: number;
    totalPnL: number;
    totalExposure: number;
  }>({
    totalUnrealizedPnL: 0,
    totalRealizedPnL: 0,
    totalPnL: 0,
    totalExposure: 0
  });
  const [risk, setRisk] = useState<{
    grossNotional: number;
    longExposure: number;
    shortExposure: number;
    netExposure: number;
    capitalUtilization: number;
    priceSensitivity1pct: number;
    priceSensitivity5pct: number;
    realizedVolRisk: number;
    inventoryByTicker: Record<string, {
      quantity: number;
      settled_quantity: number;
      utilization: number;
      exit_ratio: number;
      price_sensitivity_1pct: number;
      price_sensitivity_5pct: number;
      capital_allocated: number;
      capital_used_pct: number;
      vol_risk_1sigma: number;
    }>;
  }>({
    grossNotional: 0,
    longExposure: 0,
    shortExposure: 0,
    netExposure: 0,
    capitalUtilization: 0,
    priceSensitivity1pct: 0,
    priceSensitivity5pct: 0,
    realizedVolRisk: 0,
    inventoryByTicker: {}
  });
  
  // Chart data (TWAP/VWAP/microprice/lastPrice) lives in Redux — use store/hooks selectors.
  const chartDispatch = useAppDispatch();

  const [analyticsImbalanceBySymbol, setAnalyticsImbalanceBySymbol] = useState<Map<string, Array<{ t: number; depthImbalance: number; ofi: number }>>>(new Map());
  const [quoteSuggestions, setQuoteSuggestions] = useState<QuoteSuggestion[]>([]);
  const [inventoryBySymbol, setInventoryBySymbol] = useState<Map<string, InventoryState>>(new Map());
  const [bookAnalyticsBySecId, setBookAnalyticsBySecId] = useState<Map<string, BookAnalytics>>(new Map());
  const [statsBySecId, setStatsBySecId] = useState<Map<string, SymbolStats>>(new Map());
  const [settlementReport, setSettlementReport] = useState<SettlementReport | null>(null);
  const [performanceSnapshot, setPerformanceSnapshot] = useState<PerformanceSnapshot | null>(null);
  const [riskEvents, setRiskEvents] = useState<RiskEvent[]>([]);

  // Phase 2 state
  const [activityFeed, setActivityFeed] = useState<ActivityEvent[]>([]);
  const [regimeBySymbol, setRegimeBySymbol] = useState<Map<string, RegimeState>>(new Map());
  const [sprintState, setSprintState] = useState<SprintState | null>(null);
  const [dailySummary, setDailySummary] = useState<DailySummary | null>(null);
  const [monthlySummary, setMonthlySummary] = useState<MonthlySummary | null>(null);
  const [modelHealth, setModelHealth] = useState<MarketContextValue['modelHealth']>(null);
  const [darHealth, setDarHealth] = useState<MarketContextValue['darHealth']>({
    fixLoggedOn: false,
    fixSessionStatus: 'UNKNOWN',
    lastHeartbeatAt: null,
    pendingOrders: 0,
    instrumentsLoaded: 0,
    sessionId: null,
  });
  const [engineConfig, setEngineConfig] = useState<EngineConfig | null>(null);
  const [configAckStatus, setConfigAckStatus] = useState<'idle' | 'pending' | 'ok' | 'error'>('idle');
  const [configAckTime, setConfigAckTime] = useState<string | null>(null);
  const [configAckErrors, setConfigAckErrors] = useState<Record<string, string> | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [lastCommandAck, setLastCommandAck] = useState<CommandAckMessage | null>(null);

  const addAlert = useCallback((alert: Alert) => {
    setAlerts(prev => [alert, ...prev].slice(0, 50));
  }, []);
  const dismissAlert = useCallback((id: string) => {
    setAlerts(prev => prev.filter(a => a.id !== id));
  }, []);

  const pushActivity = useCallback((ev: ActivityEvent) => {
    setActivityFeed(prev => [ev, ...prev].slice(0, 50));
  }, []);

  const dismissDailySummary = useCallback(() => setDailySummary(null), []);

  // Refs for complex state
  const booksBySecIdRef = useRef<Map<string, BookState>>(new Map());
  const statsBySecIdRef = useRef<Map<string, Stats>>(new Map());
  const priceHistBySecIdRef = useRef<Map<string, Array<{ ts: number; price: number }>>>(new Map());
  const lastUpdateBySecIdRef = useRef<Map<string, string>>(new Map());
  const tradesBySecIdRef = useRef<Map<string, Trade[]>>(new Map());
  const lastFillBySecIdRef = useRef<Map<string, { side: 'buy'|'sell'; price: number; size: number; time?: string }>>(new Map());
  const lastMlFillUpdateMsBySecIdRef = useRef<Map<string, number>>(new Map());
  const lastOfiActivityBySecIdRef = useRef<Map<string, { value: number; ts: number }>>(new Map());
  // Per-symbol analytics history accumulated for ALL symbols (live + replay) — no re-renders
  type AnalyticsPt = { t: number; depthImbalance: number; ofi: number };
  const analyticsHistoryRef = useRef<Map<string, AnalyticsPt[]>>(new Map());
  // Last full analytics snapshot per symbol — used to prefetch on symbol switch
  const lastAnalyticsBySecIdRef = useRef<Map<string, EnhancedAnalyticsData>>(new Map());

  // Helper functions
  const ensureBook = useCallback((secId: string): BookState => {
    const m = booksBySecIdRef.current;
    let b = m.get(secId);
    if (!b) { b = { bids: new Map(), asks: new Map() }; m.set(secId, b); }
    return b;
  }, []);

  const ensureStats = useCallback((secId: string): Stats => {
    const m = statsBySecIdRef.current;
    let s = m.get(secId);
    if (!s) { s = { _cumPxSz: 0, _cumSz: 0 }; m.set(secId, s); }
    return s;
  }, []);

  const ensureSeries = useCallback((secId: string) => {
    const m = priceHistBySecIdRef.current;
    let s = m.get(secId);
    if (!s) { s = []; m.set(secId, s); }
    return s;
  }, []);

  const seedFlatSeries = useCallback((secId: string, px: number) => {
    const series = [{ ts: eatOpenToday().getTime(), price: px }, { ts: Date.now(), price: px }];
    priceHistBySecIdRef.current.set(secId, series);
    if (secId === selectedSymbol) {
      setPriceHistory(series.map(p => ({ time: fmtHHmm(p.ts), price: p.price })));
    }
  }, [selectedSymbol]);

  const appendChartPoint = useCallback((secId: string, px: number) => {
    const series = ensureSeries(secId);
    const now = Date.now();
    const last = series[series.length - 1];

    if (last && !sameEatDay(last.ts)) {
      series.length = 0;
    }

    const priceChanged = !last || Math.abs(last.price - px) > 1e-9;
    const timeElapsed = !last || (now - last.ts) >= 5000;

    if (priceChanged || timeElapsed) {
      series.push({ ts: now, price: px });
      if (series.length > 2000) series.shift();
      if (secId === selectedSymbol) {
        setPriceHistory(series.map(p => ({ time: fmtHHmm(p.ts), price: p.price })));
      }
    }
  }, [selectedSymbol, ensureSeries]);

  const extractFix = useCallback((s: string) => {
    const i = s.indexOf("8=");
    return i >= 0 ? s.slice(i) : s;
  }, []);

  // FIX message handler
  const handleFixMessage = useCallback((rawMessage: string) => {
    // JSON envelopes (e.g., BOOK_CHANGES, MARKET_STATS, TWAP from VPS)
    if (rawMessage.length && rawMessage.trim().startsWith('{')) {
      try {
        const obj = JSON.parse(rawMessage);
        const callbacks: MessageCallbacks = {
          selectedSymbol,
          setOrderBook, setLastUpdateTime, setCurrentPrice, setStats, setPriceMap,
          setAnalytics, setAnalyticsImbalanceBySymbol,
          setRecentTrades,
          setOrders,
          setPositions, setCurrentPosition, setPortfolio, setRisk,
          setQuoteSuggestions, setInventoryBySymbol, setSettlementReport, setBookAnalyticsBySecId, setStatsBySecId,
          setPerformanceSnapshot, setRiskEvents, setModelHealth,
          setRegimeBySymbol, setSprintState, setDailySummary, setMonthlySummary,
          pushActivity,
          addAlert,
          onCommandAck: (ack) => setLastCommandAck(ack),
          setDarHealth, setConfigAckStatus, setConfigAckTime, setConfigAckErrors, setEngineConfig,
          chartDispatch,
          setBookChanges,
          booksBySecIdRef, statsBySecIdRef, lastUpdateBySecIdRef, tradesBySecIdRef,
          lastFillBySecIdRef, lastMlFillUpdateMsBySecIdRef, lastOfiActivityBySecIdRef,
          analyticsHistoryRef,
          lastAnalyticsBySecIdRef,
          labelForSecId,
          getMinimalAnalytics,
          nextActivitySeq: () => _activitySeq++,
        };
        if (dispatchMessage(obj, callbacks)) return;
        // P2-5: a JSON envelope whose kind/type matched no handler was silently dropped
        // here (it falls through to FIX parsing, which ignores non-FIX text). Surface it
        // so renamed/new backend message kinds are observable instead of vanishing.
        if (obj && (obj.kind || obj.type)) {
          console.warn('[ws] unhandled message kind:', obj.kind ?? obj.type, obj);
        }

      } catch {
        // fall through to FIX parsing if not JSON
      }
    }

    // FIX parsing
    if (!rawMessage.includes("8=")) return;
    const fix = extractFix(rawMessage);
    const parsed = parseFixMessage(fix);

    // Log message
    setMessages(prev => [
      { timestamp: new Date().toLocaleTimeString(), raw: rawMessage, parsed },
      ...prev.slice(0, 19)
    ]);

    // Identify instrument
    const secId = (parsed["48"] || parsed["55"] || "").trim().toUpperCase();
    const msgType = parsed["35"];
    const entries: any[] = parsed.entries || [];

    if (!secId) return;

    // Handle execution reports
    if (msgType === "8") {
      const px = parseFloat(parsed["44"] || "0");
      const qty = parseInt(parsed["38"] || "0", 10);
      const side = parsed["54"] === "1" ? "buy" : "sell";

      const newTrade: Trade = { time: new Date().toISOString(), price: px, size: qty, side, symbol: secId };
      // persist per-symbol recent trades
      const currTrades = tradesBySecIdRef.current.get(secId) || [];
      const updatedTrades = [newTrade, ...currTrades].slice(0, 100);
      tradesBySecIdRef.current.set(secId, updatedTrades);
      if (secId === selectedSymbol) {
        setRecentTrades(updatedTrades.slice(0, 10));
      }
      if (Number.isFinite(px) && secId === selectedSymbol) setCurrentPrice(px);
      return;
    }

    // Handle stats
    const STAT_TYPES = new Set(['2','4','5','7','8','9','B','K','P','e']);
    const statEntries = entries.filter(e => STAT_TYPES.has(e.type));
    if (secId && statEntries.length) {
      const curr = ensureStats(secId);
      const next: Stats = { ...curr };

      for (const e of statEntries) {
        const px = typeof e.price === 'number' ? e.price : undefined;
        const sz = typeof e.size === 'number' ? e.size : undefined;

        switch (e.type) {
          case '2': // LAST
            if (px != null) next.last = px;
            if (e.time) next.lastTime = e.time;
            if (px != null && sz != null && sz > 0) {
              // VWAP comes from VPS
              next._cumPxSz += px * sz;
              next._cumSz += sz;
              // Removed: VWAP comes from VPS
              // if (next.vwap == null && next._cumSz > 0) {
              //   next.vwap = next._cumPxSz / next._cumSz;
              // }
              if (next.volume == null) {
                next.volume = next._cumSz;
              }
            }
            break;
          case '4': if (px != null) next.open = px; break;
          case '5': if (px != null) next.close = px; break;
          case '7': if (px != null) next.high = px; break;
          case '8': if (px != null) next.low = px; break;
          case '9': if (px != null) next.vwap = px; break;
          case 'K': if (px != null) next.close = px; break;
          case 'P': if (px != null) next.prevClose = px; break;
          case 'e': if (px != null) next.prevClose = px; break;
          case 'B': if (sz != null) next.volume = sz; break;
        }
      }

      statsBySecIdRef.current.set(secId, next);
      if (secId === selectedSymbol) {
        setStats(next);
      }
    }

    // Update price chart
    const book = ensureBook(secId);
    const { mid } = bestPrices(book);
    const s = statsBySecIdRef.current.get(secId);
    const pxForChart = (s?.vwap ?? s?.last ?? s?.prevClose ?? mid);

    if (pxForChart != null) {
      appendChartPoint(secId, pxForChart);
    }

    // Handle order book updates
    const bookEntries = entries.filter(e => e.type === "0" || e.type === "1");
    if (bookEntries.length === 0) return;

    const prevBook = ensureBook(secId);
    const next = msgType === "W" ? applySnapshot(prevBook, bookEntries) :
                 msgType === "X" ? applyIncremental(prevBook, bookEntries) : null;

    if (!next) return;

    booksBySecIdRef.current.set(secId, next);

    const midPrice = bestPrices(next).mid;
    // if (midPrice != null) setPriceMap(pm => ({ ...pm, [secId]: midPrice }));
    const sNext = statsBySecIdRef.current.get(secId);
    // store the priceMap using VWAP if available, otherwise midPrice
    const priceToStore = (sNext?.vwap ?? midPrice);
    if (priceToStore != null) setPriceMap(pm => ({ ...pm, [secId]: priceToStore }));

    if (secId === selectedSymbol) {
      const { bids, asks } = bookToArrays(next);
      setOrderBook({
        bids: bids.map(b => ({ type: "0", price: b.price, size: b.size, id: b.id })),
        asks: asks.map(a => ({ type: "1", price: a.price, size: a.size, id: a.id })),
      });
      const isoNow = new Date().toISOString();
      lastUpdateBySecIdRef.current.set(secId, isoNow);
      setLastUpdateTime(isoNow);
      if (midPrice != null) setCurrentPrice(midPrice);
    }
  }, [selectedSymbol, ensureBook, ensureStats, appendChartPoint, extractFix]);

  // Replay manager setup
  const replayManagerRef = useRef<ReplayManager | null>(null);
  const recoveryTriggeredRef = useRef(false);
  
  const handleReconnect = useCallback(async () => {
    // Wait a bit for replay manager to be initialized if needed
    let attempts = 0;
    while (!replayManagerRef.current && attempts < 20) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    if (replayManagerRef.current) {
      // Prevent duplicate recoveries
      if (recoveryTriggeredRef.current) {
        // console.log('[MarketContext] ⚠️ Recovery already triggered, skipping...');
          return;
        }
        
        recoveryTriggeredRef.current = true;
      // console.log('[MarketContext] 🔄 Starting recovery after connection...');
      
      try {
        await replayManagerRef.current.recoverAfterReconnect();
        // console.log('[MarketContext] ✅ Recovery completed');
      } catch (error) {
        console.error('[MarketContext] ❌ Error during recovery:', error);
        recoveryTriggeredRef.current = false; // Allow retry on error
      }
    } else {
      // console.warn('[MarketContext] ⚠️ Replay manager not initialized, skipping recovery');
    }
  }, []);

  // Get auth token for WebSocket connection
  const { token } = useAuth();
  
  // WebSocket connection with replay support
  const { 
    sendFixMessage, 
    sendReplayRequest, 
    getLastMessageTimestamp,
    testConnection
  } = useFixWebSocket(handleFixMessage, setConnectionStatus, undefined, handleReconnect, token, useCallback(() => {
    pushActivity({
      id: `AUTH_FAIL_${Date.now()}_${_activitySeq++}`,
      kind: 'ALERT',
      ticker: '',
      title: 'Authentication failed — please log in again',
      timestamp: new Date().toISOString(),
      severity: 'critical',
    });
  }, [pushActivity]));

  // Reset recovery flag when disconnected
  useEffect(() => {
    if (connectionStatus === 'disconnected') {
      recoveryTriggeredRef.current = false;
      // console.log('[MarketContext] 🔄 Reset recovery flag on disconnect');
    }
  }, [connectionStatus]);

  // Initialize replay manager
  useEffect(() => {
    if (!sendReplayRequest) return;

    const securities = INSTRUMENTS.map(i => i.secId);
    
    replayManagerRef.current = new ReplayManager({
      sendReplayRequest,
      getLastMessageTimestamp,
      securities,
      onOrderBookReconstructed: (secId, book) => {
        // Update the book state for this security
        booksBySecIdRef.current.set(secId, book);
        
        // If this is the selected symbol, update the UI
        if (secId === selectedSymbol) {
          const { bids, asks } = bookToArrays(book);
          setOrderBook({
            bids: bids.map(b => ({ type: "0", price: b.price, size: b.size, id: b.id })),
            asks: asks.map(a => ({ type: "1", price: a.price, size: a.size, id: a.id })),
          });
          const { mid } = bestPrices(book);
          if (mid != null) setCurrentPrice(mid);
        }
      },
      onChartDataLoaded: (secId, stream, data) => {
        // console.log(`[MarketContext] 📊 Processing ${data.length} ${stream} messages for ${secId}`);
        
        // Process historical chart data from replay
        // For replay data, we REPLACE existing data (not merge) to reconstruct charts
        if (stream === 'fix:twap') {
          // Process TWAP data - data is already filtered by secId in the replay request
          const twapData = data
            .map((msg: any) => {
              // Handle timestamp - could be in various formats
              let ts: string;
              if (msg.timestamp) {
                if (typeof msg.timestamp === 'string') {
                  ts = msg.timestamp;
                } else if (msg.timestamp < 1e12) {
                  // Unix seconds
                  ts = new Date(msg.timestamp * 1000).toISOString();
                } else {
                  // Unix milliseconds
                  ts = new Date(msg.timestamp).toISOString();
                }
              } else if (msg.id) {
                // Try to extract timestamp from message ID if available
                const idParts = String(msg.id).split('-');
                if (idParts[0] && Number.isFinite(parseFloat(idParts[0]))) {
                  const tsNum = parseFloat(idParts[0]);
                  ts = tsNum < 1e12 ? new Date(tsNum * 1000).toISOString() : new Date(tsNum).toISOString();
                } else {
                  ts = new Date().toISOString();
                }
              } else {
                ts = new Date().toISOString();
              }
              
              return {
                timestamp: ts,
                twap1m: msg.twaps?.['1m'] ?? msg.twap1m,
                twap5m: msg.twaps?.['5m'] ?? msg.twap5m,
                twap15m: msg.twaps?.['15m'] ?? msg.twap15m,
                twap30m: msg.twaps?.['30m'] ?? msg.twap30m,
                twap1h: msg.twaps?.['1hr'] ?? msg.twaps?.['1h'] ?? msg.twap1h,
                twap4h: msg.twaps?.['4hr'] ?? msg.twaps?.['4h'] ?? msg.twap4h,
                lastPrice: msg.lastPrice ?? msg.last_price
              };
            })
            .filter((d: any) => d.timestamp)
            .sort((a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

          if (twapData.length > 0) {
            chartDispatch(setTwapPoints({ secId, points: twapData }));
          }
        } else if (stream === 'fix:vwamp') {
          // Process VWAMP data - data is already filtered by secId in the replay request
          const vwampData = data
            .map((msg: any) => {
              let ts: string;
              if (msg.timestamp) {
                if (typeof msg.timestamp === 'string') {
                  ts = msg.timestamp;
                } else if (msg.timestamp < 1e12) {
                  ts = new Date(msg.timestamp * 1000).toISOString();
                } else {
                  ts = new Date(msg.timestamp).toISOString();
                }
              } else if (msg.id) {
                const idParts = String(msg.id).split('-');
                if (idParts[0] && Number.isFinite(parseFloat(idParts[0]))) {
                  const tsNum = parseFloat(idParts[0]);
                  ts = tsNum < 1e12 ? new Date(tsNum * 1000).toISOString() : new Date(tsNum).toISOString();
                } else {
                  ts = new Date().toISOString();
                }
              } else {
                ts = new Date().toISOString();
              }
              
              return {
                timestamp: ts,
                microprice: msg.vwamp ?? msg.microprice,
                imbalance: msg.imbalance ?? 0,
                spread: msg.spread,
                bidPrice: msg.bidPrice ?? msg.bid_price,
                askPrice: msg.askPrice ?? msg.ask_price
              };
            })
            .filter((d: any) => d.timestamp && Number.isFinite(d.microprice))
            .sort((a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

          if (vwampData.length > 0) {
            chartDispatch(setMicropricePoints({ secId, points: vwampData }));
          }
        } else if (stream === 'fix:marketstats') {
          // Process market stats data - extract VWAP and last price for charts
          const vwapData: Array<{ timestamp: string; vwap: number }> = [];
          const lastPriceData: Array<{ timestamp: string; lastPrice: number }> = [];
          
          data.forEach((msg: any) => {
            // Update stats
            if (msg.stats) {
              const stats = ensureStats(secId);
              const updatedStats: Stats = { ...stats };
              if (Number.isFinite(msg.stats.vwap)) updatedStats.vwap = Number(msg.stats.vwap);
              if (Number.isFinite(msg.stats.last)) updatedStats.last = Number(msg.stats.last);
              if (Number.isFinite(msg.stats.volume)) updatedStats.volume = Number(msg.stats.volume);
              if (Number.isFinite(msg.stats.open)) updatedStats.open = Number(msg.stats.open);
              if (Number.isFinite(msg.stats.high)) updatedStats.high = Number(msg.stats.high);
              if (Number.isFinite(msg.stats.low)) updatedStats.low = Number(msg.stats.low);
              if (Number.isFinite(msg.stats.close)) updatedStats.close = Number(msg.stats.close);
              if (Number.isFinite(msg.stats.prev_close)) updatedStats.prevClose = Number(msg.stats.prev_close);
              statsBySecIdRef.current.set(secId, updatedStats);
              
              if (secId === selectedSymbol) {
                setStats(updatedStats);
                if (Number.isFinite(msg.stats.last)) setCurrentPrice(Number(msg.stats.last));
              }
            }
            
            // Extract timestamp (shared for both VWAP and last price)
            let ts: string;
            if (msg.timestamp) {
              if (typeof msg.timestamp === 'string') {
                ts = msg.timestamp;
              } else if (msg.timestamp < 1e12) {
                ts = new Date(msg.timestamp * 1000).toISOString();
              } else {
                ts = new Date(msg.timestamp).toISOString();
              }
            } else if (msg.id) {
              const idParts = String(msg.id).split('-');
              if (idParts[0] && Number.isFinite(parseFloat(idParts[0]))) {
                const tsNum = parseFloat(idParts[0]);
                ts = tsNum < 1e12 ? new Date(tsNum * 1000).toISOString() : new Date(tsNum).toISOString();
              } else {
                ts = new Date().toISOString();
              }
            } else {
              ts = new Date().toISOString();
            }
            
            // Extract VWAP for chart
            if (Number.isFinite(msg.stats?.vwap)) {
              vwapData.push({
                timestamp: ts,
                vwap: Number(msg.stats.vwap)
              });
            }
            
            // Extract last price for chart
            if (Number.isFinite(msg.stats?.last)) {
              lastPriceData.push({
                timestamp: ts,
                lastPrice: Number(msg.stats.last)
              });
            }
          });
          
          if (vwapData.length > 0) {
            const sortedVwap = vwapData
              .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
              .filter((v, i, a) => a.findIndex(t => t.timestamp === v.timestamp) === i);
            chartDispatch(setVwapPoints({ secId, points: sortedVwap }));
          }

          if (lastPriceData.length > 0) {
            const sortedLastPrice = lastPriceData
              .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
              .filter((v, i, a) => a.findIndex(t => t.timestamp === v.timestamp) === i);
            chartDispatch(setLastPricePoints({ secId, points: sortedLastPrice }));
          }
        } else if (stream === 'fix:bookchanges') {
          // Process book changes data - convert to BookChangePacket format
          const bookChangesPackets: BookChangePacket[] = data
            .map((msg: any) => {
              // Handle timestamp
              let ts: string;
              if (msg.timestamp) {
                if (typeof msg.timestamp === 'string') {
                  ts = msg.timestamp;
                } else if (msg.timestamp < 1e12) {
                  ts = new Date(msg.timestamp * 1000).toISOString();
                } else {
                  ts = new Date(msg.timestamp).toISOString();
                }
              } else if (msg.id) {
                const idParts = String(msg.id).split('-');
                if (idParts[0] && Number.isFinite(parseFloat(idParts[0]))) {
                  const tsNum = parseFloat(idParts[0]);
                  ts = tsNum < 1e12 ? new Date(tsNum * 1000).toISOString() : new Date(tsNum).toISOString();
                } else {
                  ts = new Date().toISOString();
                }
              } else {
                ts = new Date().toISOString();
              }
              
              // Ensure we have the required fields
              // Replay response uses sec_id (with underscore), not secId
              // Check sec_id first (from replay), then secId, then fallback to parameter
              const packetSecId = (msg.sec_id || msg.secId || secId).toUpperCase();
              if (packetSecId !== secId.toUpperCase() && msg.sec_id) {
                // console.log(`[MarketContext] ⚠️ secId mismatch: packetSecId=${packetSecId}, expected=${secId}, msg.sec_id=${msg.sec_id}`);
              }
              // Handle changes - could be directly in msg or in msg.data
              let changes = Array.isArray(msg.changes) ? msg.changes : [];
              if (changes.length === 0 && msg.data && Array.isArray(msg.data.changes)) {
                changes = msg.data.changes;
              }
              
              // Map changes to the expected format
              const mappedChanges = changes.map((c: any) => {
                const side = (c.side || 'BID').toUpperCase() as 'BID' | 'ASK';
                const price = Number(c.price) || 0;
                const oldVal = Number(c.old) || Number(c.previous) || Number(c.prev) || 0;
                const newVal = Number(c.new) || Number(c.size) || 0;
                
                return {
                  side,
                  price,
                  old: oldVal,
                  new: newVal
                };
              }).filter((c: any) => c.price > 0 && (c.old !== c.new));
              
              return {
                kind: 'BOOK_CHANGES' as const,
                secId: packetSecId,
                symbol: msg.symbol || msg.secId,
                timestamp: ts,
                changes: mappedChanges
              } as BookChangePacket;
            })
            .filter((pkt: BookChangePacket) => 
              pkt.secId && pkt.changes && pkt.changes.length > 0
            )
            .sort((a: BookChangePacket, b: BookChangePacket) => {
              const tsA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
              const tsB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
              return tsA - tsB; // Oldest first, then we'll reverse for display
            });

          if (bookChangesPackets.length > 0) {
            // console.log(`[MarketContext] ✅ Storing ${bookChangesPackets.length} book change packets for ${secId}`);
            // console.log(`[MarketContext] Sample packet:`, bookChangesPackets[0]);
            // console.log(`[MarketContext] Packets with changes:`, bookChangesPackets.filter(p => p.changes.length > 0).length);
            
            // Group by security and replace all book changes for each security
            setBookChanges(prev => {
              // Remove existing packets for this security
              const filtered = prev.filter(pkt => pkt.secId?.toUpperCase() !== secId.toUpperCase());
              
              // Add new historical packets (oldest first, will be reversed in component)
              // Reverse to get newest first for display
              const reversed = [...bookChangesPackets].reverse();
              
              // Combine: new historical data first, then other securities' data
              const combined = [...reversed, ...filtered];
              // console.log(`[MarketContext] Total book changes after merge: ${combined.length} (${reversed.length} new for ${secId})`);
              return combined;
            });
          } else {
            // console.warn(`[MarketContext] ⚠️ No valid book change packets for ${secId} after processing`);
          }
        } else if (stream === 'fix:analytics') {
          // Process analytics history — extract depth imbalance and OFI per message
          type ImbalancePt = { t: number; depthImbalance: number; ofi: number };
          const pts: ImbalancePt[] = [];
          data.forEach((msg: any) => {
            // Derive timestamp from message id (Redis stream ID: "<unix_ms>-<seq>") or timestamp field
            let t: number;
            if (msg.timestamp) {
              const raw = Number(msg.timestamp);
              t = raw < 1e12 ? raw * 1000 : raw;
            } else if (msg.id) {
              const ms = parseFloat(String(msg.id).split('-')[0]);
              t = ms < 1e12 ? ms * 1000 : ms;
            } else {
              t = Date.now();
            }
            const analytics = msg.analytics || msg;
            const depthImbalance =
              analytics.depth?.imbalance ?? analytics.depthImbalance?.imbalance ?? 0;
            const buyFlow = analytics.orderFlow?.buyFlow ?? analytics.orderFlowImbalance?.buyFlow ?? 0;
            const sellFlow = analytics.orderFlow?.sellFlow ?? analytics.orderFlowImbalance?.sellFlow ?? 0;
            const total = buyFlow + sellFlow;
            const ofi = total > 0 ? (buyFlow - sellFlow) / total : 0;
            if (Number.isFinite(depthImbalance) || Number.isFinite(ofi)) {
              pts.push({ t, depthImbalance: Number(depthImbalance), ofi: Number(ofi) });
            }
          });
          if (pts.length > 0) {
            const sorted = pts.sort((a, b) => a.t - b.t);
            // Seed ref (used by getAnalyticsHistory) — merge with live data already in ref
            const existing = analyticsHistoryRef.current.get(secId) ?? [];
            if (sorted.length > existing.length) {
              // Replay has more history — prepend it, then append any live points newer than replay
              const replayEnd = sorted[sorted.length - 1].t;
              const liveAfterReplay = existing.filter(p => p.t > replayEnd);
              const merged = [...sorted, ...liveAfterReplay];
              analyticsHistoryRef.current.set(secId, merged.length > 5000 ? merged.slice(-5000) : merged);
            }
            // Notify AnalysisPage via state change (triggers seeding effect)
            setAnalyticsImbalanceBySymbol(prev => {
              const next = new Map(prev);
              next.set(secId, analyticsHistoryRef.current.get(secId)!);
              return next;
            });
          }
        } else if (stream === 'fix:positions') {
          // Process positions snapshot (backend already deduplicated)
          // console.log(`[MarketContext] 📊 Processing ${data.length} positions from snapshot`);
          
          // Backend returns positions already deduplicated (latest state per symbol)
          // No need to deduplicate again - just map to Position format
          const positionsData: Position[] = data
            .map((pos: any) => ({
              symbol: (pos.symbol || pos.secId || '').toUpperCase(),
              quantity: Number(pos.quantity || pos.qty || 0),
              avgPrice: Number(pos.avgPrice || pos.avg_price || 0),
              unrealizedPnL: Number(pos.unrealizedPnL || pos.unrealizedPnl || pos.unrealized_pnl || 0),
              realizedPnL: Number(pos.realizedPnL || pos.realizedPnl || pos.realized_pnl || 0),
              currentPrice: Number(pos.currentPrice || pos.lastPrice || pos.last_price || 0) || undefined,
              totalPnL: Number(pos.totalPnL || 
                        ((pos.unrealizedPnL || pos.unrealizedPnl || pos.unrealized_pnl || 0) + 
                         (pos.realizedPnL || pos.realizedPnl || pos.realized_pnl || 0))) || undefined,
              exposure: Number(pos.exposure || 
                        ((pos.quantity || pos.qty || 0) * (pos.currentPrice || pos.lastPrice || pos.last_price || 0))) || undefined,
              notional: Number(pos.notional || 
                        (Math.abs((pos.quantity || pos.qty || 0) * (pos.avgPrice || pos.avg_price || 0)))) || undefined,
              returnPercent: Number(pos.returnPercent || pos.return_percent || 0) || undefined,
              lastUpdate: pos.timestamp || pos.lastUpdate || new Date().toISOString()
            }))
            .filter((p: any) => p.symbol && p.symbol.length > 0) as Position[];
          
          if (positionsData.length > 0) {
            // console.log(`[MarketContext] ✅ Restoring ${positionsData.length} positions from snapshot:`, 
            //   positionsData.map(p => `${p.symbol}: ${p.quantity}`).join(', '));
            setPositions(positionsData);
            
            // Calculate current position for selected symbol
            const selectedPosition = positionsData.find(p => p.symbol === selectedSymbol);
            if (selectedPosition) {
              setCurrentPosition(selectedPosition.quantity);
            }
          } else {
            // console.warn(`[MarketContext] ⚠️ No valid positions found in snapshot (${data.length} positions processed)`);
          }
        } else if (stream === 'fix:positions:incremental') {
          // Process incremental position updates (apply to existing state)
          // console.log(`[MarketContext] 📊 Processing ${data.length} incremental position updates`);
          
          setPositions(prev => {
            const updated = [...prev];
            let changed = false;
            
            data.forEach((msg: any) => {
              // Replay messages come as { id, data: { position: {...}, ... } }
              // Extract the actual message data
              const msgData = msg.data || msg;
              
              // Extract position
              const position = msgData.position || msgData;
              const symbol = (position.symbol || msgData.symbol || msgData.secId || msgData.sec_id || '').toUpperCase();
              
              if (!symbol) return;
              
              const positionUpdate: Position = {
                symbol: symbol,
                quantity: Number(position.quantity || position.qty || 0),
                avgPrice: Number(position.avgPrice || position.avg_price || 0),
                currentPrice: Number(position.currentPrice || position.lastPrice || 0) || undefined,
                unrealizedPnL: Number(position.unrealizedPnL || position.unrealized_pnl || 0),
                realizedPnL: Number(position.realizedPnL || position.realized_pnl || 0),
                totalPnL: Number(position.totalPnL || 
                          ((position.unrealizedPnL || 0) + (position.realizedPnL || 0))) || undefined,
                exposure: Number(position.exposure || 0) || undefined,
                notional: Number(position.notional || 0) || undefined,
                returnPercent: Number(position.returnPercent || position.return_percent || 0) || undefined,
                lastUpdate: msgData.timestamp_iso || msgData.timestamp_unix || msg.timestamp || msg.timestamp_iso || new Date().toISOString()
              };
              
              const existingIndex = updated.findIndex(p => p.symbol === symbol);
              if (existingIndex >= 0) {
                updated[existingIndex] = positionUpdate;
                changed = true;
              } else {
                updated.push(positionUpdate);
                changed = true;
              }
            });
            
            if (changed) {
              // console.log(`[MarketContext] ✅ Applied ${data.length} incremental position updates`);
            }
            
            return updated;
          });
        } else if (stream === 'fix:trades') {
          // Process trades replay data
          // console.log(`[MarketContext] 📊 Processing ${data.length} trades from replay`);
          const tradesData: Trade[] = data
            .map((msg: any) => {
              // Handle timestamp
              let time: string;
              if (msg.timestamp) {
                if (typeof msg.timestamp === 'string') {
                  const parsed = new Date(msg.timestamp);
                  time = !Number.isNaN(parsed.getTime()) ? parsed.toISOString() : new Date().toISOString();
                } else if (msg.timestamp < 1e12) {
                  time = new Date(msg.timestamp * 1000).toISOString();
                } else {
                  time = new Date(msg.timestamp).toISOString();
                }
              } else if (msg.fix_time) {
                const parsed = new Date(msg.fix_time);
                time = !Number.isNaN(parsed.getTime()) ? parsed.toISOString() : new Date().toISOString();
              } else {
                time = new Date().toISOString();
              }
              
              return {
                time,
                price: msg.price || msg.lastPx || 0,
                size: msg.size || msg.lastQty || msg.quantity || 0,
                side: (msg.side === '1' || msg.side === 'buy' || msg.side === 'BUY') ? 'buy' as const : 'sell' as const,
                symbol: msg.symbol || msg.secId || ''
              };
            })
            .filter((t: Trade) => t.symbol && t.price > 0 && t.size > 0)
            .sort((a: Trade, b: Trade) => {
              // Sort by time descending (most recent first)
              const timeA = new Date(a.time).getTime();
              const timeB = new Date(b.time).getTime();
              return (Number.isFinite(timeB) ? timeB : 0) - (Number.isFinite(timeA) ? timeA : 0);
            });
          
          if (tradesData.length > 0) {
            // console.log(`[MarketContext] ✅ Restoring ${tradesData.length} trades from replay`);
            
            // Group trades by symbol
            const tradesBySymbol = new Map<string, Trade[]>();
            tradesData.forEach(trade => {
              const symbol = trade.symbol || '';
              if (!tradesBySymbol.has(symbol)) {
                tradesBySymbol.set(symbol, []);
              }
              tradesBySymbol.get(symbol)!.push(trade);
            });
            
            // Store trades per symbol
            tradesBySymbol.forEach((trades, symbol) => {
              tradesBySecIdRef.current.set(symbol, trades);
            });
            
            // Update recent trades for selected symbol
            const selectedTrades = tradesBySecIdRef.current.get(selectedSymbol) || [];
            setRecentTrades(selectedTrades.slice(0, 10));
          }
        } else if (stream === 'fix:orders') {
          // Process orders snapshot (backend already deduplicated)
          // console.log(`[MarketContext] 📊 Processing ${data.length} orders from snapshot`);
          
          // Backend returns orders already deduplicated (latest state per order ID)
          // No need to deduplicate again - just map to Order format
          const ordersData: Order[] = data
            .map((order: any) => {
              // Determine order status
              let status: 'new' | 'filled' | 'rejected' | 'partially_filled' = 'new';
              const ordStatus = order.ordStatus || order.orderStatus || order.status;
              if (ordStatus === '2' || ordStatus === 'FILLED' || ordStatus === 'filled') {
                status = 'filled';
              } else if (ordStatus === '1' || ordStatus === 'PARTIALLY_FILLED' || ordStatus === 'partially_filled') {
                status = 'partially_filled';
              } else if (ordStatus === '8' || ordStatus === 'REJECTED' || ordStatus === 'rejected') {
                status = 'rejected';
              }
              
              const qty = order.orderQty || order.quantity || order.qty || 0;
              const cumQty = order.cumQty || order.filledQty || order.filledQuantity || 0;
              const leavesQty = order.leavesQty ?? (qty - cumQty);
              
              // Handle timestamp
              let time: string;
              if (order.timestamp || order.time) {
                const ts = order.timestamp || order.time;
                if (typeof ts === 'string') {
                  time = ts;
                } else if (ts < 1e12) {
                  time = new Date(ts * 1000).toISOString();
                } else {
                  time = new Date(ts).toISOString();
                }
              } else {
                time = new Date().toISOString();
              }
              
              // Build fills array (backend sends complete order with fills)
              const fills: OrderFill[] = [];
              if (order.fills && Array.isArray(order.fills)) {
                fills.push(...order.fills);
              } else if (cumQty > 0) {
                // Fallback: create synthetic fill if fills not provided
                fills.push({
                  quantity: cumQty,
                  price: order.avgFillPrice || order.price || 0,
                  time: time,
                  cumQty: cumQty,
                  leavesQty: leavesQty
                });
              }
              
              return {
                id: order.id || order.clientOrderId || order.orderId || '',
                exchangeOrderId: order.exchangeOrderId || order.exchange_order_id,
                symbol: (order.symbol || order.secId || '').toUpperCase(),
                side: (order.side === '1' || order.side === 'buy' || order.side === 'BUY') ? 'buy' as const : 'sell' as const,
                qty: qty,
                price: order.price || 0,
                status: status,
                time: time,
                fills: fills,
                cumQty: cumQty,
                leavesQty: leavesQty,
                avgFillPrice: order.avgFillPrice || (cumQty > 0 ? order.price : undefined),
                lastFillTime: order.lastFillTime || (fills.length > 0 ? fills[fills.length - 1].time : undefined)
              };
            })
            .filter((o: any) => o.id && o.symbol) as Order[];
          
          if (ordersData.length > 0) {
            // console.log(`[MarketContext] ✅ Restoring ${ordersData.length} orders from snapshot:`, 
            //   ordersData.map(o => `${o.symbol} ${o.side} ${o.qty}@${o.price} [${o.status}]`).join(', '));
            setOrders(ordersData);
          } else {
            // console.warn(`[MarketContext] ⚠️ No valid orders found in snapshot (${data.length} orders processed)`);
          }
        } else if (stream === 'fix:orders:incremental') {
          // Process incremental order updates (apply to existing state)
          // console.log(`[MarketContext] 📊 Processing ${data.length} incremental order updates`);
          
          setOrders(prev => {
            const updated = [...prev];
            let changed = false;
            
            data.forEach((msg: any) => {
              // Replay messages come as { id, data: { message_data: {...}, ... } }
              // Extract the actual message data
              const msgData = msg.data || msg;
              const message_data = msgData.message_data || {};
              
              // Extract order - prefer complete order object from backend
              let order = message_data.order;
              if (!order && message_data.execution) {
                // If no complete order, try to construct from execution report
                const exec = message_data.execution;
                order = {
                  id: exec.clientOrderId,
                  exchangeOrderId: exec.orderId,
                  symbol: exec.symbol,
                  side: exec.side,
                  qty: exec.quantity || 0,
                  price: exec.price,
                  status: exec.status,
                  cumQty: exec.cumQty || 0,
                  leavesQty: exec.leavesQty || 0,
                  time: exec.time
                };
              }
              
              if (!order) {
                // Fallback to direct message
                order = msgData.order || msgData.execution || msg;
              }
              
              // Get order ID
              const orderId = order.id || order.clientOrderId || order.orderId || msg.id || '';
              if (!orderId) return;
              
              // Determine order status
              let status: 'new' | 'filled' | 'rejected' | 'partially_filled' = 'new';
              const ordStatus = order.ordStatus || order.orderStatus || order.status;
              if (ordStatus === '2' || ordStatus === 'FILLED' || ordStatus === 'filled') {
                status = 'filled';
              } else if (ordStatus === '1' || ordStatus === 'PARTIALLY_FILLED' || ordStatus === 'partially_filled') {
                status = 'partially_filled';
              } else if (ordStatus === '8' || ordStatus === 'REJECTED' || ordStatus === 'rejected') {
                status = 'rejected';
              }
              
              const qty = order.orderQty || order.quantity || order.qty || 0;
              const cumQty = order.cumQty || order.filledQty || 0;
              const leavesQty = order.leavesQty ?? (qty - cumQty);
              
              // Handle timestamp
              let time: string;
              const ts = order.timestamp || order.time || msgData.timestamp_iso || msgData.timestamp_unix || msg.timestamp || msg.timestamp_iso;
              if (ts) {
                if (typeof ts === 'string') {
                  time = ts;
                } else if (typeof ts === 'number' && ts < 1e12) {
                  time = new Date(ts * 1000).toISOString();
                } else if (typeof ts === 'number') {
                  time = new Date(ts).toISOString();
                } else {
                  time = new Date().toISOString();
                }
              } else {
                time = new Date().toISOString();
              }
              
              // Build fills array
              const fills: OrderFill[] = [];
              if (order.fills && Array.isArray(order.fills)) {
                fills.push(...order.fills);
              } else if (cumQty > 0) {
                fills.push({
                  quantity: cumQty,
                  price: order.avgFillPrice || order.price || 0,
                  time: time,
                  cumQty: cumQty,
                  leavesQty: leavesQty
                });
              }
              
              const orderUpdate: Order = {
                id: orderId,
                exchangeOrderId: order.exchangeOrderId || order.exchange_order_id,
                symbol: (order.symbol || order.secId || '').toUpperCase(),
                side: (order.side === '1' || order.side === 'buy' || order.side === 'BUY') ? 'buy' as const : 'sell' as const,
                qty: qty,
                price: order.price || 0,
                status: status,
                time: time,
                fills: fills,
                cumQty: cumQty,
                leavesQty: leavesQty,
                avgFillPrice: order.avgFillPrice || (cumQty > 0 ? order.price : undefined),
                lastFillTime: order.lastFillTime || (fills.length > 0 ? fills[fills.length - 1].time : undefined)
              };
              
              const existingIndex = updated.findIndex(o => o.id === orderId);
              if (existingIndex >= 0) {
                updated[existingIndex] = orderUpdate;
                changed = true;
              } else {
                updated.push(orderUpdate);
                changed = true;
              }
            });
            
            if (changed) {
              // console.log(`[MarketContext] ✅ Applied ${data.length} incremental order updates`);
            }
            
            return updated;
          });
        } else if (stream === 'fix:portfoliototals') {
          // Process portfolio totals snapshot
          // console.log('[MarketContext] 📊 Processing portfolio totals from snapshot:', data);
          
          // data should be the snapshot object directly (not an array)
          const snapshot = Array.isArray(data) ? data[0] : data;
          
          if (snapshot && snapshot.portfolio && snapshot.risk) {
            setPortfolio({
              totalUnrealizedPnL: Number(snapshot.portfolio.totalUnrealizedPnL ?? snapshot.portfolio.total_unrealized_pnl ?? 0),
              totalRealizedPnL: Number(snapshot.portfolio.totalRealizedPnL ?? snapshot.portfolio.total_realized_pnl ?? 0),
              totalPnL: Number(snapshot.portfolio.totalPnL ?? snapshot.portfolio.total_pnl ?? 0),
              totalExposure: Number(snapshot.portfolio.totalExposure ?? snapshot.portfolio.total_exposure ?? 0)
            });
            
            setRisk({
              grossNotional: Number(snapshot.risk.grossNotional ?? snapshot.risk.gross_notional ?? 0),
              longExposure: Number(snapshot.risk.longExposure ?? snapshot.risk.long_exposure ?? 0),
              shortExposure: Number(snapshot.risk.shortExposure ?? snapshot.risk.short_exposure ?? 0),
              netExposure: Number(snapshot.risk.netExposure ?? snapshot.risk.net_exposure ?? 0),
              capitalUtilization: Number(snapshot.risk.capitalUtilization ?? snapshot.risk.capital_utilization ?? 0),
              priceSensitivity1pct: Number(snapshot.risk.priceSensitivity1pct ?? 0),
              priceSensitivity5pct: Number(snapshot.risk.priceSensitivity5pct ?? 0),
              realizedVolRisk: Number(snapshot.risk.realizedVolRisk ?? snapshot.risk.realized_vol_risk ?? 0),
              inventoryByTicker: snapshot.risk.inventoryByTicker ?? snapshot.risk.inventory_by_ticker ?? {},
            });
            
            // console.log('[MarketContext] ✅ Restored portfolio totals from snapshot');
          }
        }
      }
    });

    // If we're already connected when replay manager is initialized, trigger recovery
    // This handles the case where the manager initializes after connection
    if (connectionStatus === 'connected' && replayManagerRef.current && !recoveryTriggeredRef.current) {
      // Small delay to ensure everything is ready
      setTimeout(() => {
        if (replayManagerRef.current && !recoveryTriggeredRef.current) {
          // console.log('[MarketContext] 🔄 Replay manager initialized while connected, triggering recovery...');
          handleReconnect();
        }
      }, 1000);
    }

    return () => {
      // Don't reset recoveryTriggeredRef here - let it reset on unmount
    };
  }, [sendReplayRequest, getLastMessageTimestamp, selectedSymbol, ensureStats, connectionStatus, handleReconnect]);

  // Update UI when selected symbol changes
  useEffect(() => {
    const b = ensureBook(selectedSymbol);
    const { bids, asks } = bookToArrays(b);
    setOrderBook({
      bids: bids.map(x => ({ type: '0', price: x.price, size: x.size, id: x.id })),
      asks: asks.map(x => ({ type: '1', price: x.price, size: x.size, id: x.id })),
    });
    const midBp = bestPrices(b).mid;
    const s = statsBySecIdRef.current.get(selectedSymbol);
    // prefer VWAP, then mid
    const preferred = (s?.vwap ?? s?.last ?? s?.prevClose ?? midBp);
    if (preferred != null) setCurrentPrice(preferred);
  }, [selectedSymbol, ensureBook]);

  // On symbol switch, proactively reconstruct latest order book snapshot for that symbol
  // so the UI does not wait for the next live ORDER_BOOK tick.
  useEffect(() => {
    if (connectionStatus !== 'connected' || !replayManagerRef.current) return;
    const secId = selectedSymbol.toUpperCase();
    let cancelled = false;

    void replayManagerRef.current.reconstructOrderBook(secId).then((book) => {
      if (cancelled || !book) return;
      booksBySecIdRef.current.set(secId, book);
      if (selectedSymbol.toUpperCase() !== secId) return;
      const { bids, asks } = bookToArrays(book);
      setOrderBook({
        bids: bids.map(b => ({ type: "0", price: b.price, size: b.size, id: b.id })),
        asks: asks.map(a => ({ type: "1", price: a.price, size: a.size, id: a.id })),
      });
      const { mid } = bestPrices(book);
      if (mid != null) setCurrentPrice(mid);
      const iso = new Date().toISOString();
      lastUpdateBySecIdRef.current.set(secId, iso);
      setLastUpdateTime(iso);
    }).catch(() => {
      // Keep existing in-memory book if replay reconstruction fails.
    });

    return () => {
      cancelled = true;
    };
  }, [selectedSymbol, connectionStatus]);

  // On symbol switch, immediately load cached analytics for the new symbol
  // so the UI doesn't show null until the next live ANALYTICS WS message.
  useEffect(() => {
    const cached = lastAnalyticsBySecIdRef.current.get(selectedSymbol);
    if (cached) {
      setAnalytics(cached);
    }
  }, [selectedSymbol]);

  useEffect(() => {
    const s = statsBySecIdRef.current.get(selectedSymbol);
    setStats(s ?? { _cumPxSz: 0, _cumSz: 0 });
  }, [selectedSymbol]);


  // Fallback performance metrics from current portfolio/orders/settlement state when backend
  // snapshot is absent or partially populated with zeros.
  useEffect(() => {
    const derivedRealized = portfolio.totalRealizedPnL !== 0
      ? portfolio.totalRealizedPnL
      : positions.reduce((s, p) => s + Number(p.realizedPnL ?? 0), 0);
    const derivedUnrealized = portfolio.totalUnrealizedPnL !== 0
      ? portfolio.totalUnrealizedPnL
      : positions.reduce((s, p) => s + Number(p.unrealizedPnL ?? 0), 0);
    const derivedQuotes = orders.length;
    const derivedFills = orders.reduce((s, o) => s + (o.fills?.length ?? 0), 0);
    const pool = settlementReport ? (Number(settlementReport.tradeable_cash ?? 0) + Number(settlementReport.committed_cash ?? 0)) : 0;
    const derivedUtil = pool > 0 && settlementReport ? Number(settlementReport.committed_cash ?? 0) / pool : 0;

    const hasDerivedSignal =
      derivedQuotes > 0
      || derivedFills > 0
      || derivedRealized !== 0
      || derivedUnrealized !== 0
      || derivedUtil > 0;
    if (!hasDerivedSignal) return;

    setPerformanceSnapshot(prev => {
      const needsPatch = !prev
        || (prev.unrealized_pnl === 0 && derivedUnrealized !== 0)
        || (prev.realized_pnl === 0 && derivedRealized !== 0)
        || (prev.quotes_generated === 0 && derivedQuotes > 0)
        || (prev.fills_processed === 0 && derivedFills > 0)
        || (prev.capital_utilization === 0 && derivedUtil > 0);
      if (!needsPatch) return prev;

      const quotes = prev?.quotes_generated && prev.quotes_generated > 0 ? prev.quotes_generated : derivedQuotes;
      const fills = prev?.fills_processed && prev.fills_processed > 0 ? prev.fills_processed : derivedFills;
      return {
        quotes_generated: quotes,
        fills_processed: fills,
        execution_rate: quotes > 0 ? fills / quotes : (prev?.execution_rate ?? 0),
        avg_spread_capture_bps: prev?.avg_spread_capture_bps ?? 0,
        sharpe_ratio: prev?.sharpe_ratio ?? null,
        capital_utilization: prev?.capital_utilization && prev.capital_utilization > 0 ? prev.capital_utilization : derivedUtil,
        max_drawdown: prev?.max_drawdown ?? 0,
        realized_pnl: prev?.realized_pnl && prev.realized_pnl !== 0 ? prev.realized_pnl : derivedRealized,
        unrealized_pnl: prev?.unrealized_pnl && prev.unrealized_pnl !== 0 ? prev.unrealized_pnl : derivedUnrealized,
        as_of: prev?.as_of ?? new Date().toISOString(),
      };
    });
  }, [orders, positions, portfolio.totalRealizedPnL, portfolio.totalUnrealizedPnL, settlementReport]);

  useEffect(() => {
    const s = statsBySecIdRef.current.get(selectedSymbol);
    const book = ensureBook(selectedSymbol);
    const { mid } = bestPrices(book);
    const px = (s?.vwap ?? s?.last ?? s?.prevClose ?? mid ?? priceMap[selectedSymbol]);

    const series = ensureSeries(selectedSymbol);
    if (!series.length && px != null && px > 0) {
      seedFlatSeries(selectedSymbol, px);
    } else {
      setPriceHistory(series.map(p => ({ time: fmtHHmm(p.ts), price: p.price })));
    }

    // Update symbol-specific last update time and recent trades in state
    const iso = lastUpdateBySecIdRef.current.get(selectedSymbol) || '';
    if (iso) setLastUpdateTime(iso);
    const trades = tradesBySecIdRef.current.get(selectedSymbol) || [];
    setRecentTrades(trades.slice(0, 10));
  }, [selectedSymbol, ensureBook, ensureSeries, seedFlatSeries, priceMap, currentPrice]);


  useEffect(() => {
    if (currentPrice === 0 && stats?.prevClose != null) {
      setCurrentPrice(stats.prevClose);
    }
  }, [currentPrice, stats?.prevClose]);

  // VWAP data comes from WebSocket only - no HTTP API fallback needed
  // The WebSocket provides real-time VWAP updates via MARKET_STATS and ANALYTICS messages
  // Historical data can be requested via replay if needed
  useEffect(() => {
    // VWAP data is populated from WebSocket messages in handleFixMessage
    // No need to fetch from HTTP API
    // If historical data is needed, use replay requests via WebSocket
  }, [selectedSymbol]);

  // (Removed: a 3s REST poll of /api/book/{sym}/levels. That endpoint does not exist
  // on the backend -- it fell through nginx to the SPA and spammed CORS errors. Live
  // order-book data comes over the WebSocket, so the REST fallback is unnecessary.)

  useEffect(() => {
    let stop = false;
    (async () => {
      // Only if no trades yet
      if ((tradesBySecIdRef.current.get(selectedSymbol) || []).length) return;

      const end = new Date();
      const start = new Date(end.getTime() - 2*60*60*1000);
      try {
        const rows = await fetchTradeSeries(selectedSymbol, start.toISOString(), end.toISOString(), '1 minute', token);
        if (stop) return;
        // Recreate a “recent trades” feel using bucket last price/volume
        const synthetic = rows
          .filter(r => r.price != null && r.volume > 0)
          .slice(-60)
          .map(r => ({ time: r.ts, price: r.price as number, size: r.volume, side: 'buy' as const, symbol: selectedSymbol }))
          .sort((a, b) => {
            const ta = new Date(a.time).getTime();
            const tb = new Date(b.time).getTime();
            return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
          });
        tradesBySecIdRef.current.set(selectedSymbol, synthetic);
        setRecentTrades(synthetic.slice(0, 10));
      } catch {}
    })();
    return () => { stop = true; };
  }, [selectedSymbol, token]);



  // Request data function
  const onRequestData = useCallback(() => {
    const testMsg = `8=FIX.4.2|35=V|49=SHABBA|56=DSE|55=${selectedSymbol}|262=1|263=1|264=1|10=000|`;
    sendFixMessage(testMsg);
    // console.log("Sent market data request for", selectedSymbol);
  }, [selectedSymbol, sendFixMessage]);

  // Place order function
  const placeOrder = useCallback((order: {
    clientOrderId: string;
    symbol: string;
    side: 'BUY' | 'SELL';
    orderType: 'LIMIT' | 'MARKET';
    price?: number;
    quantity: number;
    timeInForce?: number;
    expireDate?: string;
    expireTime?: string;
  }) => {
    // console.log('[MarketContext] 🚀 placeOrder called with:', order);
    // console.log('[MarketContext] 🔌 Connection status:', connectionStatus);

    const orderMessage = {
      type: 'PLACE_ORDER',
      order: {
        clientOrderId: order.clientOrderId,
        symbol: order.symbol.toUpperCase(),
        side: order.side,
        orderType: order.orderType,
        price: order.price,
        quantity: order.quantity,
        timeInForce: order.timeInForce ?? 1,
        expireDate: order.expireDate ?? null,
        expireTime: order.expireTime ?? null,
      }
    };
    
    const jsonMessage = JSON.stringify(orderMessage);
    // console.log('[MarketContext] 📝 Order JSON to send:', jsonMessage);
    
    const sent = sendFixMessage(jsonMessage);
    
    if (sent) {
      // console.log('[MarketContext] ✅ Order sent successfully:', orderMessage);
      // Order will appear in UI only after receiving ORDER_ACK or EXECUTION_REPORT from WebSocket
    } else {
      console.error('[MarketContext] ❌ Failed to send order - sendFixMessage returned false');
      console.error('[MarketContext] Connection status:', connectionStatus);
    }
  }, [sendFixMessage, connectionStatus]);

  // Cancel order function
  const cancelOrder = useCallback((orderId: string, symbol: string) => {
    if (connectionStatus !== 'connected') return;
    const msg = JSON.stringify({ type: 'CANCEL_ORDER', order_id: orderId, symbol: symbol.toUpperCase() });
    sendFixMessage(msg);
    // Optimistic update — mark cancelled immediately, don't wait for WS round-trip
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'cancelled' as const } : o));
  }, [sendFixMessage, connectionStatus, setOrders]);

  const updateOrderTif = useCallback((orderId: string, timeInForce: number, expireDate?: string, expireTime?: string) => {
    if (connectionStatus !== 'connected') return;
    sendFixMessage(JSON.stringify({
      type: 'UPDATE_ORDER_TIF',
      order_id: orderId,
      time_in_force: timeInForce,
      expire_date: expireDate ?? null,
      expire_time: expireTime ?? null,
    }));
    // Optimistic local update
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, timeInForce, expireDate, expireTime } : o));
  }, [sendFixMessage, connectionStatus]);

  // Send quote decision function
  const sendQuoteDecision = useCallback((quoteId: string, accepted: boolean, reason?: string) => {
    if (connectionStatus !== 'connected') {
      console.error('[MarketContext] Cannot send quote decision - WebSocket not connected');
      return false;
    }
    
    const decisionMessage: QuoteDecisionRequest = {
      type: 'QUOTE_DECISION',
      quote_id: quoteId,
      accepted,
      reason
    };
    
    const success = sendFixMessage(JSON.stringify(decisionMessage));
    if (success) {
      // Remove the quote from suggestions after decision
      setQuoteSuggestions(prev => prev.filter(q => q.quote_id !== quoteId));
    }
    return success;
  }, [connectionStatus, sendFixMessage]);

  // Send engine config update to backend
  const sendEngineConfig = useCallback((config: Partial<EngineConfig>): boolean => {
    if (connectionStatus !== 'connected') {
      console.error('[MarketContext] Cannot send CONFIG_UPDATE - WebSocket not connected');
      return false;
    }
    const msg = { type: 'CONFIG_UPDATE', ...config };
    const success = sendFixMessage(JSON.stringify(msg));
    if (success) {
      setConfigAckStatus('pending');
      setTimeout(() => {
        setConfigAckStatus(prev => prev === 'pending' ? 'error' : prev);
      }, 10000);
    }
    return success;
  }, [connectionStatus, sendFixMessage]);

  // Stable callback (was inline in contextValue — moved here to allow useMemo deps)
  const getAnalyticsHistory = useCallback(
    (secId: string) => analyticsHistoryRef.current.get(secId) ?? [],
    []
  );

  // ---------------------------------------------------------------------------
  // Memoized domain values — each re-renders only its own consumers
  // ---------------------------------------------------------------------------
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const connectionValue = React.useMemo<ConnectionContextValue>(() => ({
    connectionStatus, messages, darHealth, testWebSocket: testConnection,
  }), [connectionStatus, messages, darHealth, testConnection]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const marketDataValue = React.useMemo<MarketDataContextValue>(() => ({
    selectedSymbol, setSelectedSymbol,
    lastUpdateTime, currentPrice, stats, orderBook, priceMap, priceHistory, vwapHistory, analytics,
    recentTrades, bookChanges, analyticsImbalanceBySymbol, getAnalyticsHistory,
    bookAnalyticsBySecId, statsBySecId,
    INSTRUMENTS, labelForSecId,
  }), [selectedSymbol, lastUpdateTime, currentPrice, stats, orderBook, priceMap,
      priceHistory, vwapHistory, analytics, recentTrades, bookChanges,
      analyticsImbalanceBySymbol, getAnalyticsHistory, bookAnalyticsBySecId, statsBySecId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tradingValue = React.useMemo<TradingContextValue>(() => ({
    isAutoTrading, setIsAutoTrading, positions, orders, portfolio, risk, placeOrder, cancelOrder, updateOrderTif, onRequestData,
  }), [isAutoTrading, positions, orders, portfolio, risk, placeOrder, cancelOrder, updateOrderTif, onRequestData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const engineValue = React.useMemo<EngineContextValue>(() => ({
    quoteSuggestions, sendQuoteDecision, inventoryBySymbol, settlementReport,
    performanceSnapshot, riskEvents, activityFeed, regimeBySymbol, sprintState,
    dailySummary, monthlySummary, dismissDailySummary, modelHealth,
    engineConfig, configAckStatus, configAckTime, configAckErrors, sendEngineConfig,
  }), [quoteSuggestions, sendQuoteDecision, inventoryBySymbol, settlementReport,
      performanceSnapshot, riskEvents, activityFeed, regimeBySymbol, sprintState,
      dailySummary, monthlySummary, dismissDailySummary, modelHealth,
      engineConfig, configAckStatus, configAckTime, configAckErrors, sendEngineConfig]);

  const contextValue: MarketContextValue = {
    bookAnalyticsBySecId,
    statsBySecId,
    selectedSymbol,
    setSelectedSymbol,
    connectionStatus,
    isAutoTrading,
    setIsAutoTrading,
    lastUpdateTime,
    currentPrice,
    stats,
    orderBook,
    priceMap,
    priceHistory,
    vwapHistory,
    analytics,
    positions,
    recentTrades,
    orders,
    messages,
    bookChanges,
    onRequestData,
    placeOrder,
    cancelOrder,
    updateOrderTif,
    testWebSocket: testConnection,
    quoteSuggestions,
    sendQuoteDecision,
    inventoryBySymbol,
    settlementReport,
    performanceSnapshot,
    riskEvents,
    activityFeed,
    regimeBySymbol,
    sprintState,
    dailySummary,
    monthlySummary,
    dismissDailySummary,
    modelHealth,
    analyticsImbalanceBySymbol,
    getAnalyticsHistory,
    INSTRUMENTS,
    labelForSecId,
    portfolio,
    risk,
    sendEngineConfig,
    engineConfig,
    configAckStatus,
    configAckTime,
    configAckErrors,
    darHealth,
    alerts,
    dismissAlert,
    lastCommandAck,
  };

  return (
    <ConnectionCtx.Provider value={connectionValue}>
      <MarketDataCtx.Provider value={marketDataValue}>
        <TradingCtx.Provider value={tradingValue}>
          <EngineCtx.Provider value={engineValue}>
            <MarketContext.Provider value={contextValue}>
              {children}
            </MarketContext.Provider>
          </EngineCtx.Provider>
        </TradingCtx.Provider>
      </MarketDataCtx.Provider>
    </ConnectionCtx.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Full context — backwards compatible, consumes all domains. */
export function useMarket() {
  const ctx = useContext(MarketContext);
  if (!ctx) throw new Error('useMarket must be used within MarketProvider');
  return ctx;
}

/** Subscribe only to connection/WS state. Re-renders only on connection changes. */
export function useConnection() {
  const ctx = useContext(ConnectionCtx);
  if (!ctx) throw new Error('useConnection must be used within MarketProvider');
  return ctx;
}

/** Subscribe only to market data (book, price, analytics). */
export function useMarketData() {
  const ctx = useContext(MarketDataCtx);
  if (!ctx) throw new Error('useMarketData must be used within MarketProvider');
  return ctx;
}

/** Subscribe only to trading state (orders, positions, portfolio). */
export function useTrading() {
  const ctx = useContext(TradingCtx);
  if (!ctx) throw new Error('useTrading must be used within MarketProvider');
  return ctx;
}

/** Subscribe only to engine state (quotes, inventory, regime, activity). */
export function useEngine() {
  const ctx = useContext(EngineCtx);
  if (!ctx) throw new Error('useEngine must be used within MarketProvider');
  return ctx;
}
